import { messages } from '@/shared/constants/messages';
import {
  addDaysToDateInput,
  toDateInputValue,
  toTimeInputValue,
} from '@/shared/lib/dates';

/**
 * A regra de data do projeto, em um lugar só.
 *
 * Duas coisas, e as duas são de PRODUTO, não de formato:
 *
 * 1. **O início é de amanhã em diante.** Um projeto passa por análise humana
 *    antes de acontecer; marcar para hoje (ou para ontem) é pedir uma decisão
 *    para uma data que já não dá para decidir. É o único ponto da esteira em
 *    que a data é bloqueante — o alerta de antecedência por porte (US-20)
 *    continua NÃO bloqueante, porque lá o sistema opina sobre prazo e aqui ele
 *    constata que a data não existe mais.
 * 2. **O término é depois do início**, com data e horário contando juntos: o
 *    par vira um instante e a comparação é entre instantes. Foi assim que o
 *    banco escreveu (`projects_ends_after_starts_check`) e é assim que a RPC
 *    levanta `ends_before_starts` — as três camadas dizem a mesma coisa de
 *    propósito (§7.6).
 *
 * Mora aqui, e não dentro do schema, porque a tela precisa da MESMA regra em
 * duas formas: como erro (o Zod, que decide se o botão libera) e como limite
 * dos campos nativos (`min`, que impede a escolha antes de ela virar erro).
 * Duas implementações divergiriam no primeiro ajuste.
 *
 * O "hoje" é sempre o do fuso da igreja (regra 9): quem preenche de madrugada
 * não pode ver o formulário mudar de dia porque o navegador está em Lisboa.
 */

export interface ScheduleIssues {
  starts_at?: string;
  ends_at?: string;
}

/** `yyyy-MM-dd` do primeiro dia aceito como início: amanhã. */
export function minStartDate(now: Date = new Date()): string {
  return addDaysToDateInput(toDateInputValue(now), 1);
}

/**
 * O que está contraditório entre as duas datas — vazio quando não há nada a
 * dizer, inclusive enquanto os campos estão pela metade.
 *
 * Campo em branco NÃO é problema daqui: "falta preencher" é assunto do schema
 * de envio, e acusar contradição em campo que a pessoa ainda nem chegou é o
 * formulário brigando com quem o preenche.
 */
export function scheduleIssues(
  startsAt: string | null,
  endsAt: string | null,
  now: Date = new Date(),
): ScheduleIssues {
  const issues: ScheduleIssues = {};

  if (startsAt && toDateInputValue(startsAt) < minStartDate(now)) {
    issues.starts_at = messages.form.startInPast;
  }

  if (startsAt && endsAt && new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
    // Mesmo dia: quem errou foi o relógio, e é o relógio que a frase manda
    // corrigir. Dias distintos: foi a data.
    issues.ends_at =
      toDateInputValue(endsAt) === toDateInputValue(startsAt)
        ? messages.form.endTimeBeforeStart
        : messages.form.endDateBeforeStart;
  }

  return issues;
}

/**
 * Os limites nativos dos quatro controles da seção 3.
 *
 * `min` no `<input type="date">` apaga os dias impossíveis do calendário do
 * navegador — a pessoa não escolhe e depois lê que não podia. Ele não é
 * garantia (dá para digitar por cima, e no celular nem todo teclado respeita):
 * quem garante é `scheduleIssues`, aqui do lado.
 *
 * O `min` do horário de término só existe quando as duas pontas caem no MESMO
 * dia — num projeto de sábado a domingo, terminar às 12:00 é perfeitamente
 * válido mesmo tendo começado às 19:00.
 */
export function scheduleBounds(startsAt: string | null, now: Date = new Date()) {
  const floor = minStartDate(now);
  const startDate = startsAt ? toDateInputValue(startsAt) : null;

  return {
    startMinDate: floor,
    /** O término nunca é antes do início — e, sem início, nunca é antes de amanhã. */
    endMinDate: startDate && startDate > floor ? startDate : floor,
    /** Só morde quando o término for marcado para o mesmo dia do início. */
    endMinTimeOnDate:
      startsAt && startDate
        ? { date: startDate, time: toTimeInputValue(startsAt) }
        : null,
  };
}
